'use client';

import Link from 'next/link';
import { MarketingNav } from '@/components/marketing/MarketingNav';
import { MarketingFooter } from '@/components/marketing/MarketingFooter';

export default function SecurityPage() {
  return (
    <>
      <MarketingNav />

      <main className="pt-[56px]">
        {/* Header */}
        <section className="bg-white border-b border-[#eee]">
          <div className="max-w-[1200px] mx-auto px-6 py-16 lg:py-20">
            <h1 className="text-[36px] sm:text-[44px] font-semibold text-[#1a1a1a] leading-[1.1] tracking-[-0.02em] mb-4">
              Security
            </h1>
            <p className="text-[18px] text-[#555] leading-[1.65] max-w-[560px]">
              How we protect your email, your data, and your privacy.
            </p>
          </div>
        </section>

        {/* Encryption */}
        <section className="bg-white border-b border-[#eee]">
          <div className="max-w-[800px] mx-auto px-6 py-16 lg:py-20">
            <h2 className="text-[24px] sm:text-[28px] font-semibold text-[#1a1a1a] leading-tight mb-4">
              Encryption
            </h2>
            <p className="text-[16px] text-[#555] leading-[1.65] mb-4">
              All communication between your device and TekSpert Mail servers is encrypted using TLS 1.3, the latest version of the Transport Layer Security protocol. This ensures that your login credentials, email content, and attachments cannot be intercepted or read by third parties during transmission.
            </p>
            <p className="text-[16px] text-[#555] leading-[1.65] mb-4">
              Email data stored on our servers is encrypted at rest using AES-256, the same encryption standard used by financial institutions and government agencies. Database backups are also encrypted and stored in geographically separate locations to protect against data loss.
            </p>
            <p className="text-[16px] text-[#555] leading-[1.65]">
              Passwords are never stored in plain text. We use bcrypt with per-user salts to hash all passwords, making them computationally infeasible to reverse even in the event of a database breach.
            </p>
          </div>
        </section>

        {/* Authentication */}
        <section className="bg-[#fafafa] border-b border-[#eee]">
          <div className="max-w-[800px] mx-auto px-6 py-16 lg:py-20">
            <h2 className="text-[24px] sm:text-[28px] font-semibold text-[#1a1a1a] leading-tight mb-4">
              Authentication and access control
            </h2>
            <p className="text-[16px] text-[#555] leading-[1.65] mb-4">
              TekSpert Mail uses secure session-based authentication with JSON Web Tokens (JWT). Sessions are time-limited and automatically expire after a period of inactivity, requiring the user to re-authenticate.
            </p>
            <p className="text-[16px] text-[#555] leading-[1.65] mb-4">
              All authentication endpoints are rate-limited to prevent brute-force attacks. After a configurable number of failed login attempts, the account is temporarily locked to protect against credential stuffing.
            </p>
            <p className="text-[16px] text-[#555] leading-[1.65]">
              Email account credentials are encrypted before being stored in the database and are never exposed through the API or user interface.
            </p>
          </div>
        </section>

        {/* Infrastructure */}
        <section className="bg-white border-b border-[#eee]">
          <div className="max-w-[800px] mx-auto px-6 py-16 lg:py-20">
            <h2 className="text-[24px] sm:text-[28px] font-semibold text-[#1a1a1a] leading-tight mb-4">
              Infrastructure security
            </h2>
            <p className="text-[16px] text-[#555] leading-[1.65] mb-4">
              Our production servers run on hardened Linux systems with automatic security patching enabled. The application runs behind a reverse proxy with strict Content Security Policy (CSP) headers to prevent cross-site scripting (XSS) and other injection attacks.
            </p>
            <p className="text-[16px] text-[#555] leading-[1.65] mb-4">
              Database access is restricted to the application server only and is not exposed to the public internet. All database connections use encrypted channels and require authentication.
            </p>
            <p className="text-[16px] text-[#555] leading-[1.65]">
              We perform regular security audits of our codebase and infrastructure. Dependencies are monitored for known vulnerabilities and updated promptly when patches are available.
            </p>
          </div>
        </section>

        {/* Spam and Threats */}
        <section className="bg-[#fafafa] border-b border-[#eee]">
          <div className="max-w-[800px] mx-auto px-6 py-16 lg:py-20">
            <h2 className="text-[24px] sm:text-[28px] font-semibold text-[#1a1a1a] leading-tight mb-4">
              Spam and threat protection
            </h2>
            <p className="text-[16px] text-[#555] leading-[1.65] mb-4">
              TekSpert Mail includes built-in spam filtering that automatically identifies and separates junk mail from your inbox. Suspected spam is moved to a dedicated Spam folder where it can be reviewed and permanently deleted.
            </p>
            <p className="text-[16px] text-[#555] leading-[1.65] mb-4">
              You can manually report emails as spam or mark false positives as &ldquo;not spam&rdquo; to improve filtering accuracy for your account. These actions are also reflected on the remote IMAP server, so changes persist across all connected clients.
            </p>
            <p className="text-[16px] text-[#555] leading-[1.65]">
              Email attachments are validated on upload to prevent the distribution of potentially harmful file types. Inline HTML content in received emails is sanitised before rendering to protect against embedded scripts and tracking pixels.
            </p>
          </div>
        </section>

        {/* Privacy */}
        <section className="bg-white border-b border-[#eee]">
          <div className="max-w-[800px] mx-auto px-6 py-16 lg:py-20">
            <h2 className="text-[24px] sm:text-[28px] font-semibold text-[#1a1a1a] leading-tight mb-4">
              Privacy and data handling
            </h2>
            <p className="text-[16px] text-[#555] leading-[1.65] mb-4">
              TekSpert Mail is fully compliant with the EU General Data Protection Regulation (GDPR). We process personal data only as necessary to provide the email service and do not share user data with third parties for any purpose.
            </p>
            <p className="text-[16px] text-[#555] leading-[1.65] mb-4">
              We do not display advertisements in the application. We do not scan, analyse, or monetise the content of your emails. Our business model is based entirely on providing a quality email service — not on selling user data.
            </p>
            <p className="text-[16px] text-[#555] leading-[1.65] mb-4">
              Users can request a full export of their data or permanent deletion of their account at any time by contacting <a href="mailto:support@tekspert.co.uk" className="text-[#0078D4] hover:underline">support@tekspert.co.uk</a>.
            </p>
            <p className="text-[16px] text-[#555] leading-[1.65]">
              For full details, please read our <Link href="/privacy" className="text-[#0078D4] hover:underline">Privacy Policy</Link> and <Link href="/terms" className="text-[#0078D4] hover:underline">Terms of Service</Link>.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-[#0078D4]">
          <div className="max-w-[800px] mx-auto px-6 py-14 text-center">
            <h2 className="text-[24px] sm:text-[28px] font-semibold text-white leading-tight mb-4">
              Questions about our security practices?
            </h2>
            <p className="text-[16px] text-white/80 leading-[1.65] mb-6">
              Contact our team for more information about how we protect your data.
            </p>
            <a
              href="mailto:support@tekspert.co.uk"
              className="inline-flex px-6 py-3 bg-white text-[#0078D4] text-[15px] font-semibold rounded-[4px] hover:bg-[#f5f5f5] transition-colors"
            >
              Contact support
            </a>
          </div>
        </section>
      </main>

      <MarketingFooter />
    </>
  );
}
